import React, { useState, useEffect, useRef, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { getPost, deletePost as deletePostRequest } from "../../services/postService";
import { getPostImageUrl, getImageSrcSet } from "../../utils/urlBuilder";
import { Comment, CommentForm } from "../Comment";
import "./Post.css";
import { createEditor } from 'slate';
import { Slate, Editable, withReact } from 'slate-react';
import { withHistory } from 'slate-history';
import { ImageElement } from "./ImageElement";
import LikeButton from "./LikeButton";
import ShareButton from "./ShareButton";
import ReportButton from "../ReportButton";
import { usePostTracking, calculateScrollDepth } from "../../services/trackingService";
import { useAuth } from "../../context/AuthContext";
import { getComments as fetchCommentsForPost } from "../../services/commentService";

const DEFAULT_SLATE_CONTENT = [{ type: 'paragraph', children: [{ text: '' }] }];

const toSlateValue = (input) => {
    if (!input) {
        return DEFAULT_SLATE_CONTENT;
    }

    if (Array.isArray(input)) {
        return input.length ? input : DEFAULT_SLATE_CONTENT;
    }

    if (typeof input === 'string') {
        const trimmed = input.trim();
        if (!trimmed) {
            return DEFAULT_SLATE_CONTENT;
        }

        try {
            const parsed = JSON.parse(trimmed);
            if (Array.isArray(parsed) && parsed.length) {
                return parsed;
            }
        } catch (error) {
            // treat as plain text when JSON parsing fails
            return [{ type: 'paragraph', children: [{ text: trimmed }] }];
        }
    }

    return DEFAULT_SLATE_CONTENT;
};

const withLinks = (editor) => {
    const { isInline } = editor;

    editor.isInline = (element) => {
        if (element.type === 'link') {
            return true;
        }
        return isInline(element);
    };

    return editor;
};

const Post = () => {
    const { uuid } = useParams(); // 從路由參數中獲取 uuid
    const navigate = useNavigate();
    const { isAuthenticated } = useAuth();
    const [post, setPost] = useState(null);
    const [comments, setComments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showDropdown, setShowDropdown] = useState(false);
    const [editorValue, setEditorValue] = useState(DEFAULT_SLATE_CONTENT);
    const [editorReady, setEditorReady] = useState(false);
    const [deleteInProgress, setDeleteInProgress] = useState(false);
    const commentInputRef = useRef(null);

    // 简单的 Slate 渲染函数
    const renderElement = (props) => {
        const { element } = props;
        switch (element.type) {
            case 'link':
                return (
                    <a
                        {...props.attributes}
                        href={element.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="link-element"
                    >
                        {props.children}
                    </a>
                );
            case 'image':
                return <ImageElement {...props} />;
            case 'paragraph':
            default:
                return <p {...props.attributes}>{props.children}</p>;
        }
    };

    const renderLeaf = (props) => {
        let { children } = props;
        if (props.leaf.bold) {
            children = <strong>{children}</strong>;
        }
        if (props.leaf.italic) {
            children = <em>{children}</em>;
        }
        if (props.leaf.underline) {
            children = <u>{children}</u>;
        }
        return <span {...props.attributes}>{children}</span>;
    };

    // 创建 Slate 编辑器实例
    const editor = useMemo(() => withLinks(withHistory(withReact(createEditor()))), []);

    // Tracking related state
    const { trackView, updateTime } = usePostTracking(uuid);
    const startTimeRef = useRef(null);
    const maxScrollDepthRef = useRef(0);
    const lastUpdateTimeRef = useRef(0);
    const trackingEnabledRef = useRef(false);

    useEffect(() => {
        const fetchPost = async () => {
            try {
                const data = await getPost(uuid); // 從後端獲取貼文數據
                setPost(data);

                let initialComments = Array.isArray(data.comments) ? data.comments : [];
                try {
                    const fetchedComments = await fetchCommentsForPost(uuid);
                    if (Array.isArray(fetchedComments)) {
                        initialComments = fetchedComments;
                    }
                } catch (commentsError) {
                    console.warn("無法載入留言列表，改用貼文內建留言", commentsError);
                }

                setComments(initialComments);
                setEditorValue(toSlateValue(data?.content));
                setEditorReady(true);
            } catch (err) {
                console.error("Failed to fetch post:", err);
                setError("無法加載貼文，請稍後再試。");
                setEditorValue(DEFAULT_SLATE_CONTENT);
                setEditorReady(true);
            } finally {
                setLoading(false);
            }
        };
        setLoading(true);
        setEditorReady(false);
        fetchPost();
    }, [uuid]);

    const handleDeletePost = async () => {
        if (!post || deleteInProgress) {
            return;
        }

        const confirmed = window.confirm("確定要刪除此貼文嗎？刪除後將無法恢復。");
        if (!confirmed) {
            return;
        }

        try {
            setDeleteInProgress(true);
            await deletePostRequest(post.id);
            alert("貼文已刪除");
            setShowDropdown(false);
            if (post.topic) {
                navigate(`/topics/${post.topic}`);
            } else {
                navigate("/");
            }
        } catch (err) {
            console.error("刪除貼文失敗:", err);
            const message = err.response?.data?.detail || err.response?.data?.error || "刪除貼文失敗，請稍後再試";
            alert(message);
        } finally {
            setDeleteInProgress(false);
        }
    };

    // Post view tracking effect
    useEffect(() => {
        if (!uuid || loading || error) return;

        // 記錄開始時間
        startTimeRef.current = Date.now();
        trackingEnabledRef.current = true;

        // 記錄貼文瀏覽
        const initializeTracking = async () => {
            await trackView(); // trackView 函數會自動處理 clickedFrom
        };

        // 處理滾動事件
        const handleScroll = () => {
            if (!trackingEnabledRef.current) return;

            const currentScrollDepth = calculateScrollDepth();
            maxScrollDepthRef.current = Math.max(maxScrollDepthRef.current, currentScrollDepth);
        };

        // 發送更新（節流處理，避免過於頻繁的 API 調用）
        const sendUpdate = async () => {
            if (!trackingEnabledRef.current || !startTimeRef.current) return;

            const currentTime = Date.now();
            const dwellTime = Math.round((currentTime - startTimeRef.current) / 1000);

            // 避免過於頻繁的更新，至少間隔 5 秒
            if (currentTime - lastUpdateTimeRef.current < 5000) return;

            lastUpdateTimeRef.current = currentTime;
            await updateTime(dwellTime, maxScrollDepthRef.current);
        };

        // 處理頁面離開事件
        const handleBeforeUnload = async () => {
            if (!trackingEnabledRef.current || !startTimeRef.current) return;

            const dwellTime = Math.round((Date.now() - startTimeRef.current) / 1000);

            // 使用 sendBeacon 確保在頁面關閉時也能發送數據
            if (navigator.sendBeacon) {
                const sessionId = sessionStorage.getItem('tracking_session_id');
                const data = JSON.stringify({
                    session_id: sessionId,
                    dwell_time: dwellTime,
                    scroll_depth: maxScrollDepthRef.current
                });

                const token = localStorage.getItem("access_token");
                const headers = {
                    'Content-Type': 'application/json'
                };
                if (token) {
                    headers['Authorization'] = `Bearer ${token}`;
                }

                navigator.sendBeacon(
                    `${process.env.REACT_APP_API_URL}/posts/${uuid}/update-dwell-time/`,
                    data
                );
            } else {
                // 備用方案
                await updateTime(dwellTime, maxScrollDepthRef.current);
            }
        };

        // 定期更新（每30秒）
        const updateInterval = setInterval(sendUpdate, 30000);

        // 初始化追蹤
        initializeTracking();

        // 綁定事件監聽器
        window.addEventListener('scroll', handleScroll, { passive: true });
        window.addEventListener('beforeunload', handleBeforeUnload);

        // 清理函數
        return () => {
            trackingEnabledRef.current = false;
            clearInterval(updateInterval);
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('beforeunload', handleBeforeUnload);

            // 組件卸載時發送最終更新
            if (startTimeRef.current) {
                const dwellTime = Math.round((Date.now() - startTimeRef.current) / 1000);
                updateTime(dwellTime, maxScrollDepthRef.current);
            }
        };
    }, [uuid, loading, error, trackView, updateTime]);

    const handleNewComment = (newComment) => {
        setComments([...comments, newComment]);
    };

    const toggleDropdown = () => {
        setShowDropdown(!showDropdown);
    };

    const scrollToCommentForm = () => {
        if (!commentInputRef.current) return;
        commentInputRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        requestAnimationFrame(() => {
            if (commentInputRef.current) {
                commentInputRef.current.focus({ preventScroll: true });
            }
        });
    };

    // 點擊外部時關閉下拉選單
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (showDropdown && !event.target.closest('.post-menu')) {
                setShowDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showDropdown]);

    if (loading) return (
        <div className="card">
            <div className="post-header">
                <div className="loading-skeleton" style={{ height: '32px', borderRadius: '8px', marginBottom: '1rem' }}></div>
                <div className="loading-skeleton" style={{ height: '20px', width: '60%', borderRadius: '4px' }}></div>
            </div>
            <div className="loading-skeleton" style={{ height: '200px', margin: 0 }}></div>
            <div style={{ padding: '1.5rem' }}>
                <div className="loading-skeleton" style={{ height: '16px', marginBottom: '0.5rem', borderRadius: '4px' }}></div>
                <div className="loading-skeleton" style={{ height: '16px', marginBottom: '0.5rem', borderRadius: '4px' }}></div>
                <div className="loading-skeleton" style={{ height: '16px', width: '80%', borderRadius: '4px' }}></div>
            </div>
        </div>
    );

    if (error) return (
        <div className="card">
            <div className="error">
                ⚠️ {error}
            </div>
        </div>
    );

    if (!post) return (
        <div className="card">
            <div className="error">
                📭 貼文不存在或已被刪除
            </div>
        </div>
    );

    let contentValue;
    let contentType = 'invalid'; // 'slate', 'text', 'invalid'

    try {
        // 嘗試解析 JSON
        contentValue = typeof post.content === "string"
            ? JSON.parse(post.content)
            : post.content;

        // 檢查是否為有效的 Slate JSON 格式
        if (Array.isArray(contentValue)) {
            contentType = 'slate';
        } else if (typeof contentValue === 'string') {
            contentType = 'text';
        }
    } catch (e) {
        // JSON 解析失敗，檢查原始內容是否為字串
        if (typeof post.content === "string" && post.content.trim()) {
            contentValue = post.content;
            contentType = 'text';
        } else {
            contentValue = null;
            contentType = 'invalid';
        }
    }

    return (
        <div className="card">
            {/* 貼文頭部區域 */}
            <div className="post-header">
                <div className="post-header-content">
                    <h4>{post.title}</h4>
                    <div className="author-info">
                        <div className="author-avatar">
                            {post.author?.username ? post.author.username.charAt(0) : "?"}
                        </div>
                        <div>
                            {post.author?.username ? (
                                <Link to={`/user/${post.author.username}`}>
                                    {post.author.username}
                                </Link>
                            ) : (
                                "Unknown User"
                            )}
                        </div>
                    </div>
                    <div className="topic-tag">
                        {post.topic_name}
                    </div>
                </div>

                {/* 三點選單 */}
                <div className="post-menu">
                    <button
                        className="menu-button"
                        onClick={toggleDropdown}
                        aria-label="更多選項"
                    >
                        ⋯
                    </button>
                    {showDropdown && (
                        <div className="dropdown-menu">
                            {post?.can_delete && isAuthenticated && (
                                <button
                                    type="button"
                                    className="dropdown-item delete-button"
                                    onClick={handleDeletePost}
                                    disabled={deleteInProgress}
                                >
                                    {deleteInProgress ? "刪除中…" : "🗑️ 刪除貼文"}
                                </button>
                            )}
                            <div className="dropdown-item">
                                <ReportButton postId={post.id} isInDropdown={true} />
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* 貼文圖片 */}
            {post.image && (
                <div className="post-image-wrapper">
                    <img
                        src={getPostImageUrl(post.image, 'medium')}
                        srcSet={getImageSrcSet(post.image)}
                        sizes="(max-width: 768px) 100vw, 800px"
                        alt="Post Cover"
                        className="post-image"
                        loading="lazy"
                        decoding="async"
                    />
                </div>
            )}

            {/* 貼文內容 */}
            <div className="post-content">
                {contentType === 'slate' ? (
                    editorReady && (
                        <Slate
                            editor={editor}
                            value={editorValue}
                            initialValue={editorValue}
                            onChange={(value) => {
                                if (Array.isArray(value)) {
                                    setEditorValue(value);
                                }
                            }}
                        >
                            <Editable
                                renderElement={renderElement}
                                renderLeaf={renderLeaf}
                                readOnly={true}
                                style={{ whiteSpace: "pre-line" }}
                            />
                        </Slate>
                    )
                ) : contentType === 'text' ? (
                    <div style={{ whiteSpace: "pre-line" }}>
                        {contentValue}
                    </div>
                ) : (
                    <p>（無法顯示內容，格式錯誤）</p>
                )}
            </div>

            {/* 互動區域 */}
            <div className="post-actions">
                <div className="reaction-button">
                    <LikeButton
                        postId={post.id}
                        initialLikes={post.likes}
                        initialLiked={post.likedByCurrentUser}
                    />
                    <ShareButton
                        postTitle={post.title}
                        postUrl={window.location.href}
                    />
                </div>
            </div>

            {/* 留言區域 */}
            <div className="comments-section">
                <div className="comments-header">
                    <div className="comments-title">留言 ({comments.length})</div>
                    {isAuthenticated && (
                        <button
                            type="button"
                            className="comment-scroll-button"
                            onClick={scrollToCommentForm}
                        >
                            💬 寫留言
                        </button>
                    )}
                </div>
                {comments.length > 0 ? (
                    <div className="comments-list">
                        {comments.map((comment) => (
                            <Comment key={comment.id} comment={comment} />
                        ))}
                    </div>
                ) : (
                    <div className="no-comments">目前還沒有留言，成為第一個留言的人吧！</div>
                )}

                <div className="comment-form-wrapper">
                    <CommentForm
                        postId={post.id}
                        onCommentSubmit={handleNewComment}
                        inputRef={commentInputRef}
                    />
                </div>
            </div>
        </div>
    );
};

export default Post;
